import React, { useState, useEffect, useCallback } from "react";
import _ from "lodash";
import { useForm, Controller } from "react-hook-form";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import PhoneInput from "react-phone-input-2";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Select from "@mui/material/Select";
import ListItem from "@mui/material/ListItem";
import MenuItem from "@mui/material/MenuItem";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import { styled } from "@mui/material/styles";

import Close from "mdi-material-ui/Close";
import BottomResize from "mdi-material-ui/ResizeBottomRight";
import FileDocumentOutline from "mdi-material-ui/FileDocumentOutline";

import DefaultPalette from "@core/theme/palettes";
import FormHeader from "views/components/formheader";
import FormButtons from "views/components/formbuttons";

import "../../../assets/css/phoneInput.css";
import APICall from "network/APICall";
import EndPoints from "network/Endpoints";
import UploadMultipleImage from "./component/UploadMultipleImage";
import { isSpaceValidateTextField } from "@core/utils/Const";
import Loader from "@core/helper/Loader";

const customTheme = DefaultPalette();

const Img = styled("img")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    marginRight: theme.spacing(5),
    marginLeft: theme.spacing(2),
  },
  [theme.breakpoints.down("md")]: {
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    width: 200,
  },
}));

const HeadingTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    marginBottom: theme.spacing(2),
  },
}));

const AddBusiness = (props) => {
  const {
    open,
    toggle,
    type,
    heading,
    updateData,
    onSubmitBusiness,
    businessCategory,
    fetchBusinessList,
  } = props;

  const [files, setFiles] = useState([]);
  const [filename, setFilename] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [phoneError, setPhoneError] = useState(false);
  const [multiImage, setMultiImage] = useState([]);

  const defaultValues = {
    businessname: "",
    businessdetail: "",
    address: "",
    countrycode: "",
    phone: "",
    email: "",
    website: "",
    facebookid: "",
    instagramid: "",
    image: "",
    categoryid: "",
    service: "",
    contactperson: "",
  };

  const {
    setValue,
    reset,
    control,
    clearErrors,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({ defaultValues });

  const handleClose = () => {
    clearErrors();
    if (type === "add") {
      reset(defaultValues);
      setFiles([]);
      setFilename("");
    }
    toggle();
  };

  const resetToEmptyValues = useCallback(() => {
    setValue("businessname", "");
    setValue("businessdetail", "");
    setValue("address", "");
    setValue("countrycode", "");
    setValue("phone", "91");
    setValue("email", "");
    setValue("website", "");
    setValue("facebookid", "");
    setValue("instagramid", "");
    setValue("image", "");
    setValue("categoryid", "");
    setValue("service", "");
    setValue("contactperson", "");
    setMultiImage([]);
    setFiles([]);
  }, [setValue]);

  const resetToStoredValues = useCallback(() => {
    if (type === "edit") {
      const event = {
        businessname: updateData?.business_name,
        businessdetail: updateData?.business_details || "",
        address: updateData?.address,
        phone: `${updateData?.country_code}${updateData?.phone_no}`,
        email: updateData?.email,
        website: updateData?.website,
        facebookid: updateData?.fb_id,
        instagramid: updateData?.instagram_id,
        image: updateData?.image,
        categoryid: updateData?.business_category_id,
        services: updateData?.services || "",
        contactperson: updateData?.contact_person,
      };

      setMultiImage(updateData?.all_images);
      setCountryCode(updateData?.country_code || "");
      setFilename(event.image || "");
      setValue("businessname", event.businessname || "");
      setValue("businessdetail", event.businessdetail || "");
      setValue("address", event.address || "");
      setValue("phone", event.phone || "91");
      setValue("email", event.email || "");
      setValue("website", event.website || "");
      setValue("facebookid", event.facebookid || "");
      setValue("instagramid", event.instagramid || "");
      setValue("image", event.image || "");
      setValue("categoryid", event.categoryid || "");
      setValue("service", event.services || "");
      setValue("contactperson", event.contactperson || "");
    }
  }, [
    setValue,
    type,
    updateData?.address,
    updateData?.all_images,
    updateData?.business_category_id,
    updateData?.business_details,
    updateData?.business_name,
    updateData?.contact_person,
    updateData?.country_code,
    updateData?.email,
    updateData?.fb_id,
    updateData?.image,
    updateData?.instagram_id,
    updateData?.phone_no,
    updateData?.services,
    updateData?.website,
  ]);

  useEffect(() => {
    if (type === "edit") {
      resetToStoredValues();
    } else {
      resetToEmptyValues();
    }
  }, [open, resetToStoredValues, resetToEmptyValues, type]);

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    maxSize: 2000000,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg"],
    },
    onDrop: (acceptedFiles) => {
      setFiles(acceptedFiles.map((file) => Object.assign(file)));
      setValue("image", acceptedFiles);
    },
    onDropRejected: () => {
      setFiles([]);
      toast.error("You can only upload 1 file & maximum size of 2 MB.", {
        duration: 2000,
      });
    },
  });

  const renderFilePreview = (file) => {
    if (file.type.startsWith("image")) {
      return (
        <img
          width={38}
          height={38}
          alt={file.name}
          src={URL.createObjectURL(file)}
        />
      );
    } else {
      return <FileDocumentOutline />;
    }
  };

  const handleRemoveFile = () => {
    setFiles([]);
    setValue("image", "");
    setFilename("");
  };

  const fileList = files.map((file) => (
    <ListItem
      key={file.name}
      sx={{ justifyContent: "space-between", display: "flex" }}
    >
      <div
        className="file-details"
        style={{
          display: "flex",
          alignItems: "center",
          width: "90%",
        }}
      >
        <div className="file-preview">{renderFilePreview(file)}</div>
        <div
          style={{
            marginLeft: 2,
            flexDirection: "column",
            width: "92%",
          }}
        >
          <Typography
            className="file-name"
            variant="body2"
            sx={{ wordWrap: "break-word" }}
          >
            {file.name}
          </Typography>
          <Typography className="file-size" variant="body2">
            {Math.round(file.size / 100) / 10 > 1000
              ? `${(Math.round(file.size / 100) / 10000).toFixed(1)} mb`
              : `${(Math.round(file.size / 100) / 10).toFixed(1)} kb`}
          </Typography>
        </div>
      </div>
      <IconButton onClick={() => handleRemoveFile()}>
        <BottomResize fontSize="small" />
      </IconButton>
    </ListItem>
  ));

  const uploadBusinessImage = (id) => {
    if (multiImage.length > 0) {
      const formData = new FormData();

      if (type === "edit") {
        let check = false;
        _.map(multiImage, async (item) => {
          if (typeof item === "object") {
            check = true;
            formData.append("images", item);
          }
        });
        formData.append("id", id);
        if (check) {
          APICall("post", formData, EndPoints.uploadBusinessImage)
            .then(() => {})
            .catch(() => {});
        }
      } else {
        _.map(multiImage, (item) => {
          formData.append("images", item);
        });
        formData.append("id", id);

        APICall("post", formData, EndPoints.uploadBusinessImage)
          .then(() => {})
          .catch(() => {});
      }
    }
  };

  const removeBusinessImage = (items) => {
    const removeImages = _.filter(multiImage, (_, i) => i !== items.index);

    setMultiImage(removeImages);
    if (type === "edit" && typeof items?.imageUrl === "string") {
      var n = items?.imageUrl.lastIndexOf("/");
      var result = items?.imageUrl.substring(n + 1);
      const payload = {
        image: result,
        id: updateData?.id.toString(),
      };
      APICall("delete", payload, EndPoints.deleteBusinessImage)
        .then(() => {
          fetchBusinessList();
        })
        .catch((error) => {
          return error;
        });
    }
  };

  const submitBusinessDetails = (data, selectImage) => {
    const phoneNumber = countryCode ? data.phone.slice(countryCode.length) : "";
    const commonPayload = {
      image: files.length > 0 ? selectImage : "",
      business_name: data.businessname,
      address: data.address,
      phone_no: phoneNumber,
      contact_person: data.contactperson,
      services: data.service || "",
      email: data.email,
      website: data.website,
      fb_id: data.facebookid,
      instagram_id: data.instagramid,
      business_category_id: data.categoryid.toString(),
      business_details: data.businessdetail || "",
    };
    Loader.isLoading(true);
    if (type === "add") {
      const payload = {
        country_code: countryCode,
        ...commonPayload,
      };
      APICall("post", payload, EndPoints.addBusiness)
        .then((response) => {
          const { isError, message, data } = response.data;
          if (!isError) {
            if (multiImage.length > 0) {
              uploadBusinessImage(data[0].id);
            }
            toast.success(message);
            onSubmitBusiness();
            handleClose();
          } else {
            toast.error(message);
          }
        })
        .catch((error) => {
          return error;
        })
        .finally(() => {
          Loader.isLoading(false);
        });
    } else {
      uploadBusinessImage(updateData.id);

      const payload = {
        id: updateData.id,
        country_code: !countryCode ? updateData.country_code : countryCode,
        ...commonPayload,
      };
      APICall("post", payload, EndPoints.updateBusiness)
        .then((response) => {
          const { isError, message } = response.data;
          if (!isError) {
            toast.success(message);
            onSubmitBusiness();
            handleClose();
          } else {
            toast.error(message);
          }
        })
        .catch((error) => {
          return error;
        })
        .finally(() => {
          Loader.isLoading(false);
        });
    }
  };

  const onSubmit = (values) => {
    if (!values.image || files.length === 0) {
      submitBusinessDetails(values, "");
      return;
    }
    Loader.isLoading(true);
    const formData = new FormData();
    formData.append("image", values.image[0]);
    formData.append("type", "business");
    APICall("post", formData, EndPoints.uploadImage)
      .then((response) => {
        const { isError, data, message } = response.data;
        if (!isError) {
          submitBusinessDetails(values, data.fileName);
        } else {
          toast.error(message);
        }
      })
      .catch((error) => {
        return error;
      })
      .finally(() => {
        Loader.isLoading(false);
      });
  };

  return (
    <Drawer
      open={open}
      anchor="right"
      variant="temporary"
      onClose={handleClose}
      ModalProps={{ keepMounted: true }}
      sx={{ "& .MuiDrawer-paper": { width: { xs: 400, sm: 600 } } }}
    >
      <FormHeader heading={heading} onClick={handleClose} />
      <Box sx={{ p: 5 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="businessname"
                  control={control}
                  rules={{
                    required: true,
                    validate: isSpaceValidateTextField,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="text"
                      label="Business name"
                      placeholder="Enter business name"
                      error={Boolean(errors.businessname)}
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />
                {errors.businessname && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-first-name"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="categoryid"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <>
                      <InputLabel
                        id="validation-basic-select"
                        error={Boolean(errors.categoryid)}
                        htmlFor="validation-basic-select"
                      >
                        Business Category
                      </InputLabel>
                      <Select
                        label={"Business Category"}
                        onChange={onChange}
                        error={Boolean(errors.categoryid)}
                        labelId="validation-basic-select"
                        aria-describedby="validation-basic-select"
                        value={value}
                      >
                        {businessCategory.map((item, index) => (
                          <MenuItem value={item.id} key={index}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  )}
                />
                {errors.categoryid && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-select"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="businessdetail"
                  control={control}
                  rules={{
                    required: true,
                    validate: isSpaceValidateTextField,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      multiline
                      rows={4}
                      label="Business Details"
                      error={Boolean(errors.businessdetail)}
                      placeholder="Business Details"
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />

                {errors.businessdetail && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-select"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="address"
                  control={control}
                  rules={{
                    required: true,
                    validate: isSpaceValidateTextField,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="text"
                      label="Address / Location"
                      placeholder="Enter address"
                      error={Boolean(errors.address)}
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />
                {errors.address && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-first-name"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="phone"
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { value } }) => (
                    <PhoneInput
                      country={"in"}
                      value={value}
                      autoFormat
                      masks={{ au: "... ... ... ." }}
                      countryCodeEditable={false}
                      onChange={(value, data, event) => {
                        setCountryCode(data.dialCode);
                        setValue("phone", value);
                        setPhoneError(false);
                        clearErrors("phone");
                      }}
                      error={Boolean(errors.phone)}
                      onBlur={(e, country) => {
                        if (value && value !== countryCode && countryCode) {
                          let _length = country.format.split(".").length - 1;
                          if (value.length !== _length) {
                            let _error = { ...errors };
                            _error.isMobileEmpty = true;
                            setPhoneError(true);
                            setError("phone", "");
                          }
                        }
                      }}
                      inputClass={errors.phone && "form-control-error"}
                      buttonStyle={{
                        borderTopLeftRadius: "8px",
                        borderBottomLeftRadius: "8px",
                        margin: errors.phone ? 1 : 0,
                      }}
                      containerStyle={{
                        height: "56px",
                      }}
                      inputStyle={{
                        width: "100%",
                        borderRadius: "8px",
                        color: "rgba(76, 78, 100, 0.87)",
                        fontSize: "1rem",
                        fontWeight: 400,
                        lineHeight: "1.4375em",
                        alignItems: "center",
                        height: "56px",
                      }}
                    />
                  )}
                />
                {errors.phone && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-first-name"
                  >
                    {phoneError && "Please enter valid phone number"}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="email"
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="email"
                      label="Email"
                      placeholder="Enter email"
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="website"
                  control={control}
                  rules={{
                    required: false,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="text"
                      label="Website"
                      placeholder="Enter website"
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />
                {errors.website && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-first-name"
                  >
                    {errors.website?.message}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="facebookid"
                  control={control}
                  rules={{
                    required: false,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="text"
                      label="Facebook I'd"
                      placeholder="Enter facebook i'd"
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />
                {errors.facebookid && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-first-name"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="instagramid"
                  control={control}
                  rules={{
                    required: false,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="text"
                      label="Instagram I'd"
                      placeholder="Enter instagram i'd"
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />
                {errors.instagramid && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-first-name"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="contactperson"
                  control={control}
                  rules={{
                    required: false,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="text"
                      label="Contact Person"
                      placeholder="Enter contact person"
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />
                {errors.contactperson && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-first-name"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="service"
                  control={control}
                  rules={{
                    required: false,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      multiline
                      rows={4}
                      label="Product/Services"
                      error={Boolean(errors.service)}
                      placeholder="Enter product/services"
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />

                {errors.service && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-select"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <UploadMultipleImage
              multiImage={multiImage}
              removeBusinessImage={removeBusinessImage}
              type={type}
              setMultiImage={setMultiImage}
            />

            <Grid item xs={12}>
              {type === "edit" && files.length === 0 && filename ? (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      borderWidth: 1,
                      borderStyle: "dashed",
                      borderColor: customTheme.grey.A200,
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: 2,
                      display: "flex",
                      cursor: "pointer",
                    }}
                  >
                    <Img width={100} alt="Upload img" src={updateData.image} />
                    <IconButton onClick={() => handleRemoveFile()}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ) : files.length > 0 ? (
                <div>
                  <Box
                    sx={{
                      borderWidth: 1,
                      borderRadius: 1,
                      borderStyle: "dashed",
                      borderColor: customTheme.grey.A200,
                    }}
                  >
                    <List>{fileList}</List>
                  </Box>
                </div>
              ) : (
                <FormControl fullWidth>
                  <Controller
                    name="image"
                    control={control}
                    rules={{ required: false }}
                    render={() => (
                      <div {...getRootProps({ className: "dropzone" })}>
                        <input {...getInputProps()} />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: ["column", "column", "row"],
                            alignItems: "center",
                            borderWidth: 1,
                            borderRadius: 1,
                            borderStyle: "dashed",
                            borderColor: customTheme.grey.A200,
                          }}
                        >
                          <Img
                            width={100}
                            alt="Upload img"
                            src="/images/misc/upload.png"
                          />
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              textAlign: ["center", "center", "inherit"],
                            }}
                          >
                            <HeadingTypography>
                              Drop file here or click to upload.
                            </HeadingTypography>
                            <Typography color="textSecondary" variant="body2">
                              Allowed *.jpeg, *.jpg, *.png
                            </Typography>
                            <Typography color="textSecondary" variant="body2">
                              Max 1 file and max size of 2 MB
                            </Typography>
                          </Box>
                        </Box>
                      </div>
                    )}
                  />
                </FormControl>
              )}
            </Grid>
            <FormButtons type={type} resetFunction={resetToEmptyValues} />
          </Grid>
        </form>
      </Box>
    </Drawer>
  );
};

export default AddBusiness;
